/** Preset mapping panorama grid: 12 columns × 3 rows = 36 cells. */

export const PRESET_GRID_COLUMNS = 12;
export const PRESET_GRID_ROWS = 3;
export const PRESET_GRID_TOTAL = PRESET_GRID_COLUMNS * PRESET_GRID_ROWS;
export const PRESET_GRID_YAW_SLICE_DEG = 360 / PRESET_GRID_COLUMNS;

/**
 * Skip this fraction of panorama height above the grid (0 = use full height).
 * Grid band runs from this offset to the bottom edge.
 */
export const PRESET_GRID_FLAT_TOP_OFFSET = 1 / 2.5;

/** Vertical meridians for 360° grid overlay (one per column). */
export const PRESET_GRID_BOUNDARY_YAWS: readonly number[] = Array.from(
  { length: PRESET_GRID_COLUMNS },
  (_, i) => -180 + i * PRESET_GRID_YAW_SLICE_DEG,
);

export const PRESET_GRID_MARKER_SIZE_PX = 22;
export const PRESET_GRID_MARKER_FONT_PX = 10;

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function presetGridVisibleBand(): { top: number; bottom: number } {
  const top = clamp(PRESET_GRID_FLAT_TOP_OFFSET, 0, 0.95);
  return { top, bottom: 1 };
}

/** Equirect norm Y (0 top → 1 bottom) within the grid band, or null if above band. */
export function presetGridRowNormFromNormY(normY: number): number | null {
  const { top, bottom } = presetGridVisibleBand();
  const y = clamp(normY, 0, 0.999999);
  if (y < top) return null;
  const span = bottom - top;
  if (span <= 0) return null;
  return (y - top) / span;
}

/** Row index norm (0..1) → equirect norm Y at band center. */
export function presetGridNormYFromRowNorm(rowNorm: number): number {
  const { top, bottom } = presetGridVisibleBand();
  const span = bottom - top;
  return top + clamp(rowNorm, 0, 1) * span;
}

function presetGridBoundaryNormYs(): readonly number[] {
  const { top, bottom } = presetGridVisibleBand();
  const span = bottom - top;
  return [top + span / 3, top + (2 * span) / 3];
}

/** Horizontal grid lines in 360° view (pitch degrees). */
export function presetGridBoundaryPitches(): readonly number[] {
  return presetGridBoundaryNormYs().map((normY) => -90 + normY * 180);
}

/**
 * 360 preset display order (per row):
 * logical [1..N] → visual positions rotated by one column.
 */
export function mapLogicalColToDisplayCol(logicalCol: number): number {
  return (logicalCol + PRESET_GRID_COLUMNS - 1) % PRESET_GRID_COLUMNS;
}

export function mapDisplayColToLogicalCol(displayCol: number): number {
  return (displayCol + 1) % PRESET_GRID_COLUMNS;
}

/** Extra column shift in immersive 360° preset view (move markers back two cells). */
export const PRESET_GRID_360_COL_SHIFT = 2;

export function mapLogicalColToDisplayCol360(logicalCol: number): number {
  return (
    (logicalCol + PRESET_GRID_COLUMNS - 1 - PRESET_GRID_360_COL_SHIFT) %
    PRESET_GRID_COLUMNS
  );
}

export function mapDisplayColToLogicalCol360(displayCol: number): number {
  return (displayCol + 1 + PRESET_GRID_360_COL_SHIFT) % PRESET_GRID_COLUMNS;
}

/** Flat equirect click → grid id 1..36, or null when above the grid band. */
export function presetGridIdFromFlatNorm(
  normX: number,
  normY: number,
): number | null {
  const rowNorm = presetGridRowNormFromNormY(normY);
  if (rowNorm === null) return null;
  const x = ((normX % 1) + 1) % 1;
  const col = Math.min(
    PRESET_GRID_COLUMNS - 1,
    Math.floor(x * PRESET_GRID_COLUMNS),
  );
  const row = Math.min(
    PRESET_GRID_ROWS - 1,
    Math.floor(rowNorm * PRESET_GRID_ROWS),
  );
  return row * PRESET_GRID_COLUMNS + col + 1;
}

/** Spherical yaw/pitch (degrees) → grid id 1..36, or null above grid band. */
export function presetGridIdFromYawPitch(
  yawDeg: number,
  normY: number,
): number | null {
  const rowNorm = presetGridRowNormFromNormY(normY);
  if (rowNorm === null) return null;
  const yaw = clamp(
    ((((yawDeg + 180) % 360) + 360) % 360) - 180,
    -180,
    179.999999,
  );
  const displayCol = Math.min(
    PRESET_GRID_COLUMNS - 1,
    Math.floor((yaw + 180) / PRESET_GRID_YAW_SLICE_DEG),
  );
  const col = mapDisplayColToLogicalCol(displayCol);
  const row = Math.min(
    PRESET_GRID_ROWS - 1,
    Math.floor(rowNorm * PRESET_GRID_ROWS),
  );
  return row * PRESET_GRID_COLUMNS + col + 1;
}

/** Immersive 360° preset view: same as {@link presetGridIdFromYawPitch} with extra column shift. */
export function presetGridIdFromYawPitch360(
  yawDeg: number,
  normY: number,
): number | null {
  const rowNorm = presetGridRowNormFromNormY(normY);
  if (rowNorm === null) return null;
  const yaw = clamp(
    ((((yawDeg + 180) % 360) + 360) % 360) - 180,
    -180,
    179.999999,
  );
  const displayCol = Math.min(
    PRESET_GRID_COLUMNS - 1,
    Math.floor((yaw + 180) / PRESET_GRID_YAW_SLICE_DEG),
  );
  const col = mapDisplayColToLogicalCol360(displayCol);
  const row = Math.min(
    PRESET_GRID_ROWS - 1,
    Math.floor(rowNorm * PRESET_GRID_ROWS),
  );
  return row * PRESET_GRID_COLUMNS + col + 1;
}

export function presetGridCenter(gridId: number): {
  yawDeg: number;
  pitchDeg: number;
} {
  const idx = Math.max(0, Math.min(PRESET_GRID_TOTAL - 1, gridId - 1));
  const logicalCol = idx % PRESET_GRID_COLUMNS;
  const col = mapLogicalColToDisplayCol(logicalCol);
  const row = Math.floor(idx / PRESET_GRID_COLUMNS);
  const yawDeg = -180 + (col + 0.5) * PRESET_GRID_YAW_SLICE_DEG;
  const rowNorm = (row + 0.5) / PRESET_GRID_ROWS;
  const normY = presetGridNormYFromRowNorm(rowNorm);
  const pitchDeg = -90 + normY * 180;
  return { yawDeg, pitchDeg };
}

/** Immersive 360° preset view: marker position with extra column shift. */
export function presetGridCenter360(gridId: number): {
  yawDeg: number;
  pitchDeg: number;
} {
  const idx = Math.max(0, Math.min(PRESET_GRID_TOTAL - 1, gridId - 1));
  const logicalCol = idx % PRESET_GRID_COLUMNS;
  const col = mapLogicalColToDisplayCol360(logicalCol);
  const row = Math.floor(idx / PRESET_GRID_COLUMNS);
  const yawDeg = -180 + (col + 0.5) * PRESET_GRID_YAW_SLICE_DEG;
  const rowNorm = (row + 0.5) / PRESET_GRID_ROWS;
  const normY = presetGridNormYFromRowNorm(rowNorm);
  const pitchDeg = -90 + normY * 180;
  return { yawDeg, pitchDeg };
}
